
import { defineComponent, PropType } from "vue";
import HeroBalanceCard from "@/components/dashboard/sequence/HeroBalanceCard.vue";
import { getAccountDetailsData } from "@/api/dashboard";

interface DashboardUser {
  avatar?: string;
  fname?: string;
  lname?: string;
  usid?: string;
  address?: string;
  email?: string;
  mobile?: string;
}

interface DashboardTab {
  to: string;
  label: string;
}

const OVI_DASHBOARD_TABS: DashboardTab[] = [
  { to: "/dashboard/reminderspage", label: "Reminders" },
  { to: "/dashboard/accountinfo", label: "Account Info" },
  { to: "/dashboard/recentactivity", label: "Recent Activity" },
  { to: "/dashboard/globalpartners", label: "Global Partners" },
  { to: "/dashboard/primemonitoring", label: "Prime Monitoring" },
  { to: "/dashboard/mycoverage", label: "My Coverage" },
  { to: "/dashboard/coveragelist", label: "Available Coverage" },
  { to: "/dashboard/packagepointslist", label: "Package Points" },
];

export const ECP_DASHBOARD_TABS: DashboardTab[] = [
  { to: "/dashboardecp/reminderspage", label: "Reminders" },
  { to: "/dashboardecp/accountinfo", label: "Account Info" },
  { to: "/dashboardecp/recentactivity", label: "Recent Activity" },
  { to: "/dashboardecp/globalpartners", label: "Global Partners" },
  { to: "/dashboardecp/primemonitoring", label: "Prime Monitoring" },
  { to: "/dashboardecp/packagepointslistecp", label: "Package Points" },
];

export default defineComponent({
  name: "DashboardProfileHeader",
  components: {
    HeroBalanceCard,
  },
  props: {
    user: {
      type: Object as PropType<DashboardUser>,
      default: () => ({}),
    },
    coverPhoto: {
      type: String,
      default: "media/cover/unstoppabull.png",
    },
    bio: { type: String, default: "" },
    rankPrimeImage: { type: String, default: "" },
    rankPrime: { type: String, default: "" },
    currentRank: { type: String, default: "" },
    showRankImage: { type: Boolean, default: true },
    showAddAccount: { type: Boolean, default: false },
    showTabs: { type: Boolean, default: true },
    showLifetimeIncome: { type: Boolean, default: true },
    lifetimeLabel: { type: String, default: "Lifetime Income" },
    lifetimeAmount: { type: [Number, String], default: 0 },
    lifetimeCurrency: { type: String, default: "" },
    profileRoute: { type: String, default: "/settings/profile" },
    tabs: {
      type: Array as PropType<DashboardTab[]>,
      default: () => OVI_DASHBOARD_TABS,
    },
  },
  emits: ["add-account"],
  data() {
    return {
      menuOpen: false,
      achievements: [] as Record<string, unknown>[],
    };
  },
  computed: {
    avatarAlt(): string {
      const name = [this.user.fname, this.user.lname].filter(Boolean).join(" ");
      return name ? `${name} profile photo` : "Profile photo";
    },
    displayBio(): string {
      if (this.bio) {
        return this.bio;
      }
      if (this.user.usid) {
        return `Account ID · ${this.user.usid}`;
      }
      return "";
    },
    displayAchievements(): string[] {
      return this.achievements
        .map((item) => this.getAchievementLabel(item))
        .filter(Boolean);
    },
    displayPhone(): string {
      return this.user.mobile ? String(this.user.mobile) : "";
    },
  },
  mounted() {
    document.addEventListener("click", this.handleDocumentClick);
    this.loadAchievements();
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
  },
  methods: {
    async loadAchievements() {
      try {
        const response = await getAccountDetailsData();
        const items = response?.data?.achievements;

        if (Array.isArray(items)) {
          this.achievements = items;
        }
      } catch (error) {
        console.error("Failed to load profile achievements:", error);
      }
    },
    getAchievementLabel(item: unknown): string {
      if (typeof item === "string") {
        return item.trim();
      }

      if (!item || typeof item !== "object") {
        return "";
      }

      const record = item as Record<string, unknown>;
      const labelKeys = ["achievement", "label", "name", "title", "description"];

      for (const key of labelKeys) {
        const value = record[key];
        if (value != null && String(value).trim()) {
          return String(value).trim();
        }
      }

      for (const value of Object.values(record)) {
        if (value != null && String(value).trim()) {
          return String(value).trim();
        }
      }

      return "";
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    onAddAccount() {
      this.closeMenu();
      this.$emit("add-account");
    },
    handleDocumentClick(event: MouseEvent) {
      const root = this.$refs.menuRoot as HTMLElement | undefined;
      if (root && !root.contains(event.target as Node)) {
        this.closeMenu();
      }
    },
  },
});
